import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fulfillmentApi } from '../api/fulfillment-api';
import { ErrorState } from '../components/ErrorState';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { requirementLabel, type FulfillmentTemplate } from '../domain/fulfillment';
import { TemplateEditorDrawer } from '../features/fulfillment/TemplateEditorDrawer';
import { useFulfillmentReferences } from '../features/fulfillment/use-fulfillment-references';

/** Order templates: named installation configurations (tidaltech/koi#8081). */
export function FulfillmentTemplatesPage() {
  const references = useFulfillmentReferences();
  const [editing, setEditing] = useState<FulfillmentTemplate | 'new' | null>(null);
  const templates = useQuery({
    queryKey: ['fulfillment-templates', 'all'],
    queryFn: () => fulfillmentApi.listTemplates(true),
  });

  if (templates.isLoading) return <PageLoadingState label="Loading templates…" />;
  if (templates.isError) {
    return (
      <ErrorState
        title="Templates could not be loaded"
        rpcName="ListFulfillmentTemplates"
        onRetry={() => void templates.refetch()}
      />
    );
  }
  const rows = [...(templates.data ?? [])].sort(
    (a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name),
  );

  return (
    <>
      <PageHeader
        backLink={<Link to="/fulfillment">← Pick & pack</Link>}
        title="Order templates"
        description="Installation configurations new orders can start from. Editing a template never changes existing orders."
        actions={
          <button type="button" className="primary" onClick={() => setEditing('new')}>
            New template
          </button>
        }
      />
      {editing ? (
        <TemplateEditorDrawer
          template={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {rows.length === 0 ? (
        <div className="empty-state">
          <strong>No templates yet.</strong>
          <button type="button" className="primary" onClick={() => setEditing('new')}>
            New template
          </button>
        </div>
      ) : (
        <div className="table-wrap reference-table">
          <table>
            <thead>
              <tr>
                <th scope="col">Template</th>
                <th scope="col">Lines</th>
                <th scope="col">Status</th>
                <th scope="col" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((template) => (
                <tr key={template.id}>
                  <th scope="row">
                    {template.name}
                    {template.description ? (
                      <p className="table-subtext">{template.description}</p>
                    ) : null}
                  </th>
                  <td>
                    <ul className="compact-list">
                      {template.requirements.map((requirement, index) => (
                        <li key={index}>
                          {requirement.quantity} ×{' '}
                          {requirementLabel(requirement, {
                            part: references.partName,
                            stock: references.stockName,
                          })}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td>
                    <span
                      className={
                        template.active
                          ? 'state-badge state-badge-ready'
                          : 'state-badge state-badge-muted'
                      }
                    >
                      {template.active ? 'Available' : 'Retired'}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setEditing(template)}
                      aria-label={`Edit ${template.name}`}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
